// ui/crt-shader.js: M5 Change #8 (Path B, CSS overlay).
//
// Toggles the .crt-subtle / .crt-full classes on <html>. CSS in
// styles/crt.css does the actual rendering (scanlines, vignette,
// phosphor glow, flicker). Path A (WebGL shader) is a future
// upgrade that swaps these classes for a canvas overlay.
//
// Accessibility hard requirements (§4.6):
//   • prefers-reduced-motion: reduce → force tier to 'off' (no overlay).
//     Flicker never shows. (Static scanlines/vignette are removed too,
//     to err on the side of safety for motion-sensitive users.)
//   • prefers-contrast: more → vignette + glow disabled by CSS media query.
//     Scanlines remain (carry semantic info, not pure decoration).

/** @typedef {'off'|'subtle'|'full'} CrtTier */

const CLASS_SUBTLE = 'crt-subtle';
const CLASS_FULL   = 'crt-full';

/**
 * Effective tier given user-set tier and reduced-motion preference.
 *
 * @param {CrtTier} desired
 * @param {boolean} reducedMotion
 * @returns {CrtTier}
 */
export function effectiveTier(desired, reducedMotion) {
  if (reducedMotion) return 'off';
  if (desired !== 'off' && desired !== 'subtle' && desired !== 'full') return 'off';
  return desired;
}

/**
 * Apply the CRT tier to the document.
 *
 * @param {CrtTier} tier
 * @param {{ doc?: Document }} [opts]
 */
export function applyCrtTier(tier, opts = {}) {
  const doc = opts.doc || (typeof document !== 'undefined' ? document : null);
  if (!doc || !doc.documentElement) return;
  const html = doc.documentElement;
  // Always strip both classes; re-add only if the chosen tier matches.
  html.classList.remove(CLASS_SUBTLE);
  html.classList.remove(CLASS_FULL);
  if (tier === 'subtle') html.classList.add(CLASS_SUBTLE);
  else if (tier === 'full') html.classList.add(CLASS_FULL);
}

/**
 * @param {{ window?: Window }} [opts]
 * @returns {boolean}
 */
export function prefersReducedMotion(opts = {}) {
  const w = opts.window || (typeof window !== 'undefined' ? window : null);
  if (!w || !w.matchMedia) return false;
  try {
    return w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export const _internals = { CLASS_SUBTLE, CLASS_FULL };
